/**
 * F14.3 — EL PARTE DEL TÉCNICO, CON Y SIN CONEXIÓN
 *
 * Una sola forma para la pantalla de campo, venga de donde venga el parte (el servidor en línea o el paquete del
 * dispositivo): `VistaParteCampo` = el parte tal como lo dio el servidor + las operaciones de este dispositivo que
 * aún no se reflejan en él (proyección; el dato del servidor nunca se sobrescribe). Mismo criterio que
 * `mis-trabajos-local.ts` para Limpieza.
 *
 * Aquí también se registran las operaciones (`MNT_INICIAR`, `MNT_NOTA`, `MNT_CONSUMO`, `MNT_FINALIZAR` y la foto,
 * `EVIDENCIA_SUBIR` con ámbito PT). Son precomprobaciones de UX —capacidad del perfil, caducidad, asignación, una
 * intervención abierta, stock descargado— nunca autoridad: el servidor revalida todo con el dominio de siempre.
 *
 * MATERIAL: «previsto» (lo que el parte dice que hará falta) nunca es «consumido» (lo que se registra). El parte de
 * mantenimiento no tiene hoy material previsto propio (deuda F14.3-MNT-PREVISTO): la pantalla lo dice así, y enseña
 * aparte el stock DESCARGADO (una fotografía con su hora) y los consumos.
 *
 * STOCK: la fotografía no es autoridad. Un consumo sin red es una INTENCIÓN: sólo resta en la ESTIMACIÓN que se
 * enseña («Disponible descargado: 4 · Pendiente de sincronizar: −1 · Disponible estimado: 3»). Al sincronizar manda
 * el ledger: si ya no queda, el servidor no lo registra (CONFLICTO_STOCK → «Necesita revisión»), nunca negativo.
 *
 * CADUCIDAD (`caducidad.ts`): la unidad es el PARTE. «Iniciado» en este dispositivo = tenía una intervención mía
 * abierta al descargar, o hay alguna operación local sobre ese parte (cualquier estado). Con más de 72 h no se inicia
 * un parte no iniciado; lo iniciado se puede continuar y terminar. Abrir o mirar un parte no lo inicia.
 */
import type { ConsumoCampo, NotaCampo, ParteCampo, PaqueteMantenimiento, StockCampo, TipoNotaCampo } from '../api.ts';
import { decidirPorCaducidad, nivelCaducidad } from './caducidad.ts';
import {
  guardarBlobEvidencia,
  insertarMutacion,
  leerMantenimientoLocal,
  leerMeta,
  listarMutacionesDeOt,
  listarMutacionesNoSincronizadas,
  listarMutacionesSincronizadasRecientes,
  type MantenimientoLocal,
  type Mutacion,
  type ParteCampoLocal,
  type PayloadEvidenciaSubir,
  type PayloadMntConsumo,
  type PayloadMntFinalizar,
  type PayloadMntIniciar,
  type PayloadMntNota,
  type PayloadMutacion,
  type ResultadoMntMutacion,
  type TipoMutacion,
} from './db.ts';
import { presentarEstado, type PresentacionEstado } from './estados-sync.ts';
import { calcularHashSha256, comprimirSiProcede, hayEspacioSuficiente, LIMITE_EVIDENCIA_BYTES } from './mutaciones.ts';
import { definicionDe } from './operaciones.ts';

// ================================================================ proyección

const GUARDADO_SERVIDOR: PresentacionEstado = { estado: 'SINCRONIZADO', texto: 'Guardado en el servidor', clase: 'exito-suave' };

/** Pendiente de llegar al servidor (y todavía puede llegar): cuenta para lo que se enseña y para la estimación. */
export function enCamino(m: Pick<Mutacion, 'estadoSync'>): boolean {
  return m.estadoSync === 'PENDIENTE' || m.estadoSync === 'SINCRONIZANDO' || m.estadoSync === 'ERROR_REINTENTABLE';
}
const enRevision = (m: Pick<Mutacion, 'estadoSync'>): boolean => m.estadoSync === 'EN_ESPERA' || m.estadoSync === 'RECHAZADA';

export interface IntervencionCampo {
  /** Id real, o el `idOperacionCliente` de su inicio si todavía no llegó al servidor. */
  readonly clave: string;
  readonly idReal: string | null;
  /** Si se inició en este dispositivo: su operación (las notas y el fin dependen de ella mientras no tenga id real). */
  readonly idOperacionInicio: string | null;
  readonly operario: string;
  readonly mia: boolean;
  readonly inicioEn: string;
  readonly finEn: string | null;
  readonly resultado: string | null;
  readonly trabajoRealizado: string | null;
  readonly guardado: PresentacionEstado;
  readonly finGuardado: PresentacionEstado | null;
}

export interface NotaVista {
  readonly clave: string;
  readonly claveIntervencion: string;
  readonly tipo: TipoNotaCampo;
  readonly texto: string;
  readonly ocurridoEn: string;
  readonly autor: string | null;
  readonly guardado: PresentacionEstado;
}

export interface ConsumoVista {
  readonly clave: string;
  readonly articulo: string;
  readonly almacen: string;
  readonly cantidad: string;
  readonly unidad: string;
  readonly lote: string | null;
  /** «Pendiente de validar», «Validado»… o, si aún está en el dispositivo, su estado de sincronización. */
  readonly estado: string;
  readonly guardado: PresentacionEstado;
  /** Lo que dijo el servidor si no lo registró (p. ej. sin stock). */
  readonly motivo: string | null;
}

export interface FotoVista {
  readonly clave: string;
  readonly contexto: string;
  readonly guardado: PresentacionEstado;
}

export interface VistaParteCampo {
  readonly campo: ParteCampo;
  /** Estado del parte con lo de este dispositivo encima (p. ej. EN_CURSO tras iniciar sin red). */
  readonly estado: string;
  readonly intervenciones: readonly IntervencionCampo[];
  readonly notas: readonly NotaVista[];
  readonly consumos: readonly ConsumoVista[];
  readonly fotos: readonly FotoVista[];
  /** Mi intervención abierta (del servidor o iniciada aquí), o `null`. */
  readonly abierta: IntervencionCampo | null;
  /** Otras intervenciones abiertas de otros técnicos (para no ofrecer finalizar el parte). */
  readonly otrasAbiertas: number;
  /** Caducidad: ¿está iniciado en este dispositivo? (ver la cabecera). */
  readonly iniciado: boolean;
  /** Operaciones locales de este parte que necesitan revisión. */
  readonly enRevision: number;
  /** Operaciones locales de este parte aún sin llegar al servidor. */
  readonly pendientes: number;
  readonly fueraDeVentana: boolean;
}

const TEXTO_ESTADO_CONSUMO: Readonly<Record<string, string>> = {
  BORRADOR: 'Borrador',
  PENDIENTE_VALIDACION: 'Pendiente de validar',
  VALIDADO: 'Validado',
  RECHAZADO: 'Rechazado',
};

function marcadorDe(m: Mutacion): string {
  //  Nota/fin: `idEntidad` es la intervención real o, con `dependeDe`, el marcador de su inicio.
  return m.dependeDe ?? m.idEntidad;
}

/**
 * El parte (servidor o paquete) + las operaciones locales de ESE parte, en `ordenLocal`. Una operación ya
 * SINCRONIZADA sólo se proyecta si su efecto aún no está en el parte (el paquete se descargó antes).
 */
export function proyectarParte(
  campo: ParteCampo & { fueraDeVentana?: boolean },
  operaciones: readonly Mutacion[],
  stock: PaqueteMantenimiento['stock'] | null = null,
): VistaParteCampo {
  const p = campo.parte;
  const intervenciones: IntervencionCampo[] = p.intervenciones.map((i) => ({
    clave: i.id,
    idReal: i.id,
    idOperacionInicio: null,
    operario: i.operario.nombre,
    mia: p.miIntervencionAbierta === i.id,
    inicioEn: i.inicioEn,
    finEn: i.finEn,
    resultado: i.resultado,
    trabajoRealizado: i.trabajoRealizado,
    guardado: GUARDADO_SERVIDOR,
    finGuardado: i.finEn === null ? null : GUARDADO_SERVIDOR,
  }));
  const notas: NotaVista[] = campo.notas.map((n: NotaCampo) => ({
    clave: n.id, claveIntervencion: n.idIntervencion, tipo: n.tipo, texto: n.texto, ocurridoEn: n.ocurridoEn, autor: n.autor, guardado: GUARDADO_SERVIDOR,
  }));
  const consumos: ConsumoVista[] = campo.consumos.map((c: ConsumoCampo) => ({
    clave: c.id,
    articulo: `${c.articulo.codigo} · ${c.articulo.descripcion}`,
    almacen: c.almacen.descripcion,
    cantidad: c.cantidad,
    unidad: c.unidad,
    lote: c.lote,
    estado: TEXTO_ESTADO_CONSUMO[c.estado] ?? c.estado.toLowerCase(),
    guardado: GUARDADO_SERVIDOR,
    motivo: null,
  }));
  const fotos: FotoVista[] = [];
  const idsIntervencion = new Set(p.intervenciones.map((i) => i.id));
  const idsNota = new Set(campo.notas.map((n) => n.id));
  const idsConsumo = new Set(campo.consumos.map((c) => c.id));
  let estado: string = p.estado;
  let pendientes = 0;
  let revision = 0;

  //  Intervención por id real o por el marcador de su inicio local.
  const buscar = (clave: string): number =>
    intervenciones.findIndex((i) => i.clave === clave || i.idReal === clave || i.idOperacionInicio === clave);

  for (const m of [...operaciones].sort((a, b) => a.ordenLocal - b.ordenLocal)) {
    const r: ResultadoMntMutacion | null = m.resultadoMnt ?? null;
    const sincronizada = m.estadoSync === 'SINCRONIZADA';
    if (enCamino(m)) pendientes += 1;
    if (enRevision(m)) revision += 1;
    const guardado = presentarEstado(m);
    switch (m.tipo) {
      case 'MNT_INICIAR': {
        if (sincronizada && r?.idIntervencion != null && idsIntervencion.has(r.idIntervencion)) break;
        const previa = r?.idIntervencion == null ? -1 : buscar(r.idIntervencion);
        if (previa >= 0) break;
        intervenciones.push({
          clave: r?.idIntervencion ?? m.idOperacionCliente,
          idReal: r?.idIntervencion ?? null,
          idOperacionInicio: m.idOperacionCliente,
          operario: 'Tú',
          mia: true,
          inicioEn: r?.horaEfectiva ?? m.fechaEfectiva,
          finEn: null,
          resultado: null,
          trabajoRealizado: null,
          guardado,
          finGuardado: null,
        });
        if ((enCamino(m) || sincronizada) && ['ABIERTO', 'PLANIFICADO', 'PENDIENTE', 'REABIERTO'].includes(estado)) estado = 'EN_CURSO';
        break;
      }
      case 'MNT_FINALIZAR': {
        const i = buscar(marcadorDe(m));
        if (i < 0) break;
        const actual = intervenciones[i]!;
        if (sincronizada && actual.finEn !== null && actual.finGuardado === GUARDADO_SERVIDOR) break;
        const pl = m.payload as PayloadMntFinalizar;
        if (enRevision(m)) {
          intervenciones[i] = { ...actual, finGuardado: guardado };
          break;
        }
        intervenciones[i] = {
          ...actual,
          finEn: r?.horaEfectiva ?? m.fechaEfectiva,
          resultado: pl.resultado,
          trabajoRealizado: pl.trabajoRealizado,
          finGuardado: guardado,
        };
        if (sincronizada && r !== null) estado = r.estadoPt;
        else if (!intervenciones.some((x) => x.finEn === null)) {
          if (pl.decision === 'PENDIENTE') estado = 'PENDIENTE';
          else if (pl.decision === 'FINALIZAR') estado = 'FINALIZADO';
        }
        break;
      }
      case 'MNT_NOTA': {
        if (sincronizada && r?.idNota != null && idsNota.has(r.idNota)) break;
        const pl = m.payload as PayloadMntNota;
        const i = buscar(marcadorDe(m));
        notas.push({
          clave: m.idOperacionCliente,
          claveIntervencion: i < 0 ? marcadorDe(m) : intervenciones[i]!.clave,
          tipo: pl.tipo,
          texto: pl.texto,
          ocurridoEn: r?.horaEfectiva ?? m.fechaEfectiva,
          autor: null,
          guardado,
        });
        break;
      }
      case 'MNT_CONSUMO': {
        if (sincronizada && r?.idConsumo != null && idsConsumo.has(r.idConsumo)) break;
        const pl = m.payload as PayloadMntConsumo;
        //  El nombre del material sale de la fotografía de stock (de donde se eligió); sin ella, el rótulo local.
        const fila = stock?.filas.find((x) => x.idAlmacen === pl.idAlmacen && x.idArticulo === pl.idArticulo) ?? null;
        const almacen = stock?.almacenes.find((a) => a.id === pl.idAlmacen) ?? null;
        consumos.push({
          clave: m.idOperacionCliente,
          articulo: fila !== null ? `${fila.codigo} · ${fila.descripcion}` : (m.rotuloLocal ?? 'Material').replace(/^Consumo · /, ''),
          almacen: almacen?.descripcion ?? '',
          cantidad: pl.cantidad,
          unidad: fila?.unidad.codigo ?? '',
          lote: pl.idLote === null ? null : (fila?.lotes.find((l) => l.id === pl.idLote)?.codigo ?? null),
          estado: sincronizada ? TEXTO_ESTADO_CONSUMO['PENDIENTE_VALIDACION']! : guardado.texto,
          guardado,
          motivo: enRevision(m) ? (m.ultimoError?.mensaje ?? null) : null,
        });
        break;
      }
      case 'EVIDENCIA_SUBIR': {
        if (sincronizada) break;
        fotos.push({ clave: m.idOperacionCliente, contexto: (m.payload as PayloadEvidenciaSubir).contexto, guardado });
        break;
      }
      default:
        break;
    }
  }

  //  Notas en el orden de los hechos (la hora efectiva), nunca en el de registro.
  notas.sort((a, b) => a.ocurridoEn.localeCompare(b.ocurridoEn));
  const abierta = intervenciones.find((i) => i.mia && i.finEn === null && !enRevisionPresentacion(i.guardado)) ?? null;
  const otrasAbiertas = intervenciones.filter((i) => !i.mia && i.finEn === null).length;
  return {
    campo,
    estado,
    intervenciones,
    notas,
    consumos,
    fotos,
    abierta,
    otrasAbiertas,
    iniciado: p.miIntervencionAbierta !== null || operaciones.length > 0,
    enRevision: revision,
    pendientes,
    fueraDeVentana: campo.fueraDeVentana === true,
  };
}

const enRevisionPresentacion = (g: PresentacionEstado): boolean => g.estado === 'NECESITA_REVISION' || g.estado === 'ESPERANDO_OTRA';

// ================================================================ stock

export interface EstimacionStock {
  readonly fila: StockCampo;
  /** Lo descargado: saldo menos lo pendiente de validar que ya se veía al descargar. */
  readonly descargado: number;
  /** Consumos de este dispositivo que aún no estaban en la fotografía (en negativo al enseñarlo). */
  readonly pendiente: number;
  /** Sólo una estimación: manda el ledger al sincronizar. */
  readonly estimado: number;
}

/** ¿Este consumo local ya está contado en la fotografía? (sólo si llegó al servidor ANTES de descargarla). */
function cuentaEnEstimacion(m: Mutacion, fotografiadoEn: string): boolean {
  if (m.tipo !== 'MNT_CONSUMO') return false;
  if (enCamino(m)) return true;
  return m.estadoSync === 'SINCRONIZADA' && m.creadoLocalEn > fotografiadoEn;
}

export function estimarStock(stock: PaqueteMantenimiento['stock'], operaciones: readonly Mutacion[]): EstimacionStock[] {
  return stock.filas.map((fila) => {
    const pendiente = operaciones
      .filter((m) => cuentaEnEstimacion(m, stock.fotografiadoEn))
      .map((m) => m.payload as PayloadMntConsumo)
      .filter((pl) => pl.idAlmacen === fila.idAlmacen && pl.idArticulo === fila.idArticulo)
      .reduce((s, pl) => s + Number(pl.cantidad), 0);
    const descargado = Number(fila.disponible);
    return { fila, descargado, pendiente, estimado: descargado - pendiente };
  });
}

/** «Stock actualizado a las 08:17» (o con el día, si no es de hoy). */
export function textoFotografiaStock(fotografiadoEn: string, ahora: Date = new Date()): string {
  const d = new Date(fotografiadoEn);
  const hora = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const mismoDia = d.toDateString() === ahora.toDateString();
  return mismoDia ? `Stock actualizado a las ${hora}` : `Stock actualizado el ${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} a las ${hora}`;
}

/** Cantidad sin ceros sobrantes («4», «1,5»). */
export function cantidad(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString('es-ES', { maximumFractionDigits: 6 });
}

// ================================================================ lectura local

export interface DatosCampoLocal {
  readonly mantenimiento: MantenimientoLocal;
  readonly operaciones: readonly Mutacion[];
}

/** Todo el paquete del técnico y las operaciones relevantes (las sin resolver y las recientes ya guardadas). */
export async function leerCampoLocal(idPerfil: string): Promise<DatosCampoLocal | null> {
  const mantenimiento = await leerMantenimientoLocal(idPerfil);
  if (mantenimiento === null) return null;
  const [sinResolver, recientes] = await Promise.all([
    listarMutacionesNoSincronizadas(idPerfil),
    listarMutacionesSincronizadasRecientes(idPerfil, 200),
  ]);
  const operaciones = [...sinResolver, ...recientes].filter((m) => esDeMantenimiento(m.tipo, m.payload));
  return { mantenimiento, operaciones };
}

export function esDeMantenimiento(tipo: TipoMutacion, payload: PayloadMutacion): boolean {
  return tipo.startsWith('MNT_') || (tipo === 'EVIDENCIA_SUBIR' && (payload as PayloadEvidenciaSubir).ambito === 'PT');
}

/** Las operaciones locales de UN parte (todas, cualquier estado): la proyección y «iniciado». */
export async function operacionesDeParte(idPerfil: string, idPt: string): Promise<Mutacion[]> {
  return (await listarMutacionesDeOt(idPerfil, idPt)).filter((m) => esDeMantenimiento(m.tipo, m.payload));
}

// ================================================================ registrar operaciones

export type ResultadoRegistro =
  | { readonly ok: true; readonly mutacion: Mutacion; readonly aviso: string | null }
  | { readonly ok: false; readonly motivo: string };

/** Lo que una operación necesita saber del parte: de donde lo esté viendo el técnico (servidor o dispositivo). */
export interface EntornoParte {
  readonly vista: VistaParteCampo;
  /** `DISPOSITIVO` = datos del paquete: aplica la caducidad. `SERVIDOR` = recién leídos en línea. */
  readonly fuente: 'SERVIDOR' | 'DISPOSITIVO';
  /** Otra intervención mía abierta en OTRO parte (código), si la hay: una sola a la vez en el dispositivo. */
  readonly abiertaEnOtroParte: string | null;
}

/** Datos de una operación lista para viajar (en línea) o encolarse (sin red): mismo contrato en los dos caminos. */
export interface OperacionMnt {
  readonly tipo: TipoMutacion;
  readonly idEntidad: string;
  readonly idPt: string;
  readonly idCentro: string;
  readonly payload: PayloadMutacion;
  readonly revisionBase: string | null;
  readonly fechaEfectiva: string;
  readonly dependeDe: string | null;
  readonly rotuloLocal: string;
}

type Preparada = { readonly ok: true; readonly op: OperacionMnt; readonly aviso: string | null } | { readonly ok: false; readonly motivo: string };

const ESTADOS_SIN_TRABAJO = new Set(['CERRADO', 'ANULADO', 'FINALIZADO']);
export const MOTIVO_PARTE_NO_ASIGNADO = 'Este parte no está asignado a ti: no puedes registrar trabajo en él.';

function comprobarParte(e: EntornoParte, nuevo: boolean): string | null {
  const p = e.vista.campo.parte;
  if (e.vista.fueraDeVentana) return 'Este parte ya no venía en los últimos datos del servidor (reasignado, anulado o cerrado). Revísalo con conexión.';
  if (!p.soyTecnicoAsignado) return MOTIVO_PARTE_NO_ASIGNADO;
  if (p.estado === 'ANULADO') return 'Este parte está anulado.';
  if (p.estado === 'CERRADO') return 'Este parte está cerrado.';
  if (nuevo && ESTADOS_SIN_TRABAJO.has(e.vista.estado)) return 'Este parte ya está finalizado: no admite intervenciones nuevas.';
  return null;
}

export type MetaCaducidad = { readonly generadoEn: string; readonly validoOperativamenteHasta: string; readonly bloqueaTrabajoNuevoDesde?: string | null } | null;

function comprobarCaducidadParte(e: EntornoParte, meta: MetaCaducidad, iniciado: boolean): { ok: true; aviso: string | null } | { ok: false; motivo: string } {
  if (e.fuente === 'SERVIDOR' || meta === null) return { ok: true, aviso: null };
  const decision = decidirPorCaducidad(nivelCaducidad(meta), iniciado);
  return decision.permitido ? { ok: true, aviso: decision.aviso } : { ok: false, motivo: decision.motivo };
}

/** Destino de una nota/fin/consumo: la intervención abierta (id real, o el marcador de su inicio local). */
function destino(e: EntornoParte): { idEntidad: string; dependeDe: string | null } | null {
  const a = e.vista.abierta;
  if (a === null) return null;
  return a.idReal !== null ? { idEntidad: a.idReal, dependeDe: null } : { idEntidad: a.idOperacionInicio!, dependeDe: a.idOperacionInicio };
}

const activosConocidos = (e: EntornoParte) => e.vista.campo.activos.map((a) => ({ id: a.id, estado: a.estado }));

function base(e: EntornoParte, tipo: TipoMutacion, idEntidad: string, payload: PayloadMutacion, dependeDe: string | null, rotulo: string, fecha?: string): OperacionMnt {
  const p = e.vista.campo.parte;
  return {
    tipo, idEntidad, idPt: p.id, idCentro: p.centro.id, payload, revisionBase: p.actualizadoEn,
    fechaEfectiva: fecha ?? new Date().toISOString(), dependeDe, rotuloLocal: `${rotulo} · ${p.titulo}`,
  };
}

const SIN_INTERVENCION = 'Primero inicia la intervención.';

export function prepararInicio(e: EntornoParte, meta: MetaCaducidad): Preparada {
  const fallo = comprobarParte(e, true);
  if (fallo !== null) return { ok: false, motivo: fallo };
  if (e.vista.abierta !== null) return { ok: false, motivo: 'Ya tienes una intervención abierta en este parte: continúa esa.' };
  if (e.abiertaEnOtroParte !== null) {
    return { ok: false, motivo: `Tienes otra intervención abierta en ${e.abiertaEnOtroParte}: finalízala antes de empezar otra.` };
  }
  const cad = comprobarCaducidadParte(e, meta, e.vista.iniciado);
  if (!cad.ok) return cad;
  const payload: PayloadMntIniciar = { activos: activosConocidos(e) };
  return { ok: true, op: base(e, 'MNT_INICIAR', e.vista.campo.parte.id, payload, null, 'Iniciar intervención'), aviso: cad.aviso };
}

export function prepararNota(e: EntornoParte, meta: MetaCaducidad, tipo: TipoNotaCampo, texto: string): Preparada {
  const fallo = comprobarParte(e, false);
  if (fallo !== null) return { ok: false, motivo: fallo };
  const d = destino(e);
  if (d === null) return { ok: false, motivo: SIN_INTERVENCION };
  const limpio = texto.trim();
  if (limpio === '') return { ok: false, motivo: 'Escribe la nota.' };
  if (limpio.length > 4000) return { ok: false, motivo: 'La nota es demasiado larga (máximo 4000 caracteres).' };
  const cad = comprobarCaducidadParte(e, meta, true);
  if (!cad.ok) return cad;
  const payload: PayloadMntNota = { tipo, texto: limpio };
  const rotulo = tipo === 'DIAGNOSTICO' ? 'Diagnóstico' : tipo === 'TRABAJO' ? 'Trabajo realizado' : 'Observación';
  return { ok: true, op: base(e, 'MNT_NOTA', d.idEntidad, payload, d.dependeDe, rotulo), aviso: cad.aviso };
}

export interface DatosConsumo {
  readonly fila: StockCampo;
  readonly idLote: string | null;
  readonly cantidad: string;
  readonly observaciones: string | null;
  /** El disponible ESTIMADO de esa fila ahora (para avisar, nunca para decidir). */
  readonly estimado: number;
}

export function prepararConsumo(e: EntornoParte, meta: MetaCaducidad, c: DatosConsumo): Preparada {
  const fallo = comprobarParte(e, false);
  if (fallo !== null) return { ok: false, motivo: fallo };
  const d = destino(e);
  if (d === null) return { ok: false, motivo: SIN_INTERVENCION };
  //  Lotes/series (decisión F14.3): sin red no se inventa trazabilidad. Un serializado se consume en línea desde la
  //  ficha; un artículo con lote exige elegir uno de los lotes descargados.
  if (c.fila.controlaSeries) return { ok: false, motivo: `${c.fila.codigo} se consume por números de serie: regístralo con conexión desde la ficha del parte.` };
  if (c.fila.controlaLotes && c.idLote === null) return { ok: false, motivo: `${c.fila.codigo} se controla por lotes: elige el lote.` };
  if (c.idLote !== null && !c.fila.lotes.some((l) => l.id === c.idLote)) return { ok: false, motivo: 'Ese lote no está en el stock descargado.' };
  const texto = c.cantidad.trim().replace(',', '.');
  const decimales = c.fila.unidad.decimales;
  const patron = decimales > 0 ? new RegExp(`^\\d+(\\.\\d{1,${String(Math.min(6, decimales))}})?$`) : /^\d+$/;
  if (!patron.test(texto) || Number(texto) <= 0) {
    return { ok: false, motivo: decimales > 0 ? `Indica una cantidad mayor que cero (hasta ${String(decimales)} decimales).` : 'Indica una cantidad entera mayor que cero.' };
  }
  const cad = comprobarCaducidadParte(e, meta, true);
  if (!cad.ok) return cad;
  const payload: PayloadMntConsumo = {
    idAlmacen: c.fila.idAlmacen,
    idArticulo: c.fila.idArticulo,
    idLote: c.idLote,
    idUnidad: c.fila.unidad.id,
    cantidad: texto,
    idIntervencion: d.idEntidad,
    observaciones: c.observaciones !== null && c.observaciones.trim() !== '' ? c.observaciones.trim() : null,
  };
  //  Más de lo que se estima disponible: se guarda igual (es lo que se ha usado de verdad), pero se avisa de que el
  //  servidor puede no registrarlo.
  const supera = Number(texto) > c.estimado
    ? `Supera el disponible estimado (${cantidad(Math.max(0, c.estimado))} ${c.fila.unidad.codigo}): al sincronizar, el servidor puede no registrarlo si no queda stock.`
    : null;
  const aviso = [cad.aviso, supera].filter((x): x is string => x !== null).join(' ') || null;
  return { ok: true, op: base(e, 'MNT_CONSUMO', e.vista.campo.parte.id, payload, d.dependeDe, `Consumo · ${c.fila.codigo} ${c.fila.descripcion}`), aviso };
}

export interface DatosFin {
  readonly trabajoRealizado: string;
  readonly resultado: PayloadMntFinalizar['resultado'] | '';
  readonly decision: PayloadMntFinalizar['decision'];
  readonly motivoPendiente: string | null;
  readonly detallePendiente: string | null;
}

/** Las mismas reglas que el diálogo en línea (`partes.tsx::DialogoFinalizarIntervencion`), sin cerrar el parte. */
export function opcionesFin(v: VistaParteCampo, resultado: string): PayloadMntFinalizar['decision'][] {
  const o: PayloadMntFinalizar['decision'][] = ['CONTINUAR'];
  if (v.otrasAbiertas === 0) {
    o.push('PENDIENTE');
    if (resultado === 'RESUELTO') o.push('FINALIZAR');
  }
  return o;
}

export function prepararFin(e: EntornoParte, meta: MetaCaducidad, f: DatosFin): Preparada {
  const fallo = comprobarParte(e, false);
  if (fallo !== null) return { ok: false, motivo: fallo };
  const d = destino(e);
  if (d === null) return { ok: false, motivo: SIN_INTERVENCION };
  const trabajo = f.trabajoRealizado.trim();
  if (trabajo.length < 5) return { ok: false, motivo: 'Describe el trabajo realizado (al menos 5 caracteres).' };
  if (f.resultado === '') return { ok: false, motivo: 'Elige el resultado.' };
  if (!opcionesFin(e.vista, f.resultado).includes(f.decision)) return { ok: false, motivo: 'Ese siguiente paso no es posible ahora.' };
  if (f.decision === 'PENDIENTE') {
    if (f.motivoPendiente === null || f.motivoPendiente === '') return { ok: false, motivo: 'Indica por qué queda pendiente.' };
    if (f.motivoPendiente === 'OTRO' && (f.detallePendiente ?? '').trim().length < 5) return { ok: false, motivo: 'Explica el motivo (al menos 5 caracteres).' };
  }
  const cad = comprobarCaducidadParte(e, meta, true);
  if (!cad.ok) return cad;
  const payload: PayloadMntFinalizar = {
    trabajoRealizado: trabajo,
    resultado: f.resultado,
    decision: f.decision,
    motivoPendiente: f.decision === 'PENDIENTE' ? f.motivoPendiente : null,
    detallePendiente: f.decision === 'PENDIENTE' && (f.detallePendiente ?? '').trim() !== '' ? f.detallePendiente!.trim() : null,
    activos: activosConocidos(e),
  };
  return { ok: true, op: base(e, 'MNT_FINALIZAR', d.idEntidad, payload, d.dependeDe, 'Finalizar intervención'), aviso: cad.aviso };
}

/** Foto: a la intervención abierta si la hay (o al parte); la caducidad, por el parte. */
export function prepararFoto(e: EntornoParte, meta: MetaCaducidad): { ok: true; idIntervencion: string | null; dependeDe: string | null; aviso: string | null } | { ok: false; motivo: string } {
  const fallo = comprobarParte(e, false);
  if (fallo !== null) return { ok: false, motivo: fallo };
  const cad = comprobarCaducidadParte(e, meta, e.vista.iniciado || e.vista.abierta !== null);
  if (!cad.ok) return cad;
  const a = e.vista.abierta;
  return { ok: true, idIntervencion: a?.idReal ?? null, dependeDe: a !== null && a.idReal === null ? a.idOperacionInicio : null, aviso: cad.aviso };
}

/**
 * Encola una operación ya preparada. La capa de persistencia repite las dos comprobaciones que un doble toque o una
 * pantalla desfasada podrían saltarse: el perfil es de TÉCNICO, y nunca dos inicios en camino sobre el mismo parte.
 */
export async function encolarOperacion(
  idPerfil: string,
  op: OperacionMnt,
  opciones: { readonly idOperacionCliente?: string; readonly aplicadaEnServidor?: ResultadoMntMutacion } = {},
): Promise<ResultadoRegistro> {
  const meta = await leerMeta(idPerfil);
  if (meta === null) return { ok: false, motivo: 'Todavía no has preparado este dispositivo para trabajar sin conexión.' };
  if (meta.rol !== 'TECNICO') return { ok: false, motivo: 'Tu perfil de este dispositivo no puede registrar trabajo de mantenimiento.' };
  const invalido = definicionDe(op.tipo)?.validarLocal?.(op.payload) ?? null;
  if (invalido !== null) return { ok: false, motivo: invalido };
  if (op.tipo === 'MNT_INICIAR' && opciones.aplicadaEnServidor === undefined) {
    //  Doble toque, dos pestañas o una pantalla desfasada: un inicio que aún no llegó al servidor y no tiene su fin
    //  registrado sigue abierto. (Lo ya sincronizado lo decide la vista, con lo que dice el servidor.)
    const deParte = await operacionesDeParte(idPerfil, op.idPt);
    const fines = new Set(deParte.filter((f) => f.tipo === 'MNT_FINALIZAR' && f.dependeDe !== null).map((f) => f.dependeDe));
    const abierto = deParte.some((i) => i.tipo === 'MNT_INICIAR' && enCamino(i) && !fines.has(i.idOperacionCliente));
    if (abierto) return { ok: false, motivo: 'Ya hay una intervención iniciada en este parte desde este dispositivo.' };
  }
  const mutacion = await insertarMutacion(idPerfil, {
    tipo: op.tipo,
    idEntidad: op.idEntidad,
    idOt: op.idPt,
    idCentro: op.idCentro,
    payload: op.payload,
    revisionBase: op.revisionBase,
    fechaEfectiva: op.fechaEfectiva,
    dependeDe: op.dependeDe,
    rotuloLocal: op.rotuloLocal,
    ...(opciones.idOperacionCliente === undefined ? {} : { idOperacionCliente: opciones.idOperacionCliente }),
    ...(opciones.aplicadaEnServidor === undefined ? {} : { aplicadaEnServidor: opciones.aplicadaEnServidor }),
  });
  return { ok: true, mutacion, aviso: null };
}

export interface DatosFoto {
  readonly archivo: File;
  readonly contexto: 'ANTES' | 'DURANTE' | 'DESPUES' | 'GENERAL';
  readonly idIntervencion: string | null;
  readonly dependeDe: string | null;
  readonly idPt: string;
  readonly idCentro: string;
  readonly titulo: string;
  readonly idOperacionCliente?: string;
}

/** La foto al almacén de evidencias de siempre: comprimida, con hash, cifrada y con cuota (FASE 5.4). Nunca base64. */
export async function encolarFoto(idPerfil: string, f: DatosFoto): Promise<ResultadoRegistro> {
  const meta = await leerMeta(idPerfil);
  if (meta === null) return { ok: false, motivo: 'Todavía no has preparado este dispositivo para trabajar sin conexión.' };
  if (meta.rol !== 'TECNICO') return { ok: false, motivo: 'Tu perfil de este dispositivo no puede registrar trabajo de mantenimiento.' };
  if (f.archivo.size === 0) return { ok: false, motivo: 'El fichero está vacío.' };
  if (f.archivo.size > LIMITE_EVIDENCIA_BYTES) return { ok: false, motivo: `El fichero pesa más de ${String(Math.floor(LIMITE_EVIDENCIA_BYTES / (1024 * 1024)))} MB.` };
  if (!(await hayEspacioSuficiente(f.archivo.size))) return { ok: false, motivo: 'No hay espacio suficiente en este dispositivo para guardar la foto.' };
  const mime = f.archivo.type !== '' ? f.archivo.type : 'application/octet-stream';
  const archivo = await comprimirSiProcede(f.archivo, mime);
  const ahora = new Date().toISOString();
  const payload: PayloadEvidenciaSubir = {
    ambito: 'PT',
    contexto: f.contexto,
    descripcion: null,
    tomadaEn: ahora,
    visibilidad: 'INTERNA',
    hashSha256: await calcularHashSha256(archivo),
    mime: archivo.type !== '' ? archivo.type : mime,
    tamanoBytes: archivo.size,
    nombreArchivo: f.archivo.name !== '' ? f.archivo.name : 'foto.jpg',
    idIntervencion: f.idIntervencion,
  };
  const mutacion = await insertarMutacion(idPerfil, {
    tipo: 'EVIDENCIA_SUBIR',
    idEntidad: f.idPt,
    idOt: f.idPt,
    idCentro: f.idCentro,
    payload,
    revisionBase: null,
    fechaEfectiva: ahora,
    dependeDe: f.dependeDe,
    rotuloLocal: `Foto · ${f.titulo}`,
    ...(f.idOperacionCliente === undefined ? {} : { idOperacionCliente: f.idOperacionCliente }),
  });
  await guardarBlobEvidencia(idPerfil, mutacion.idOperacionCliente, archivo);
  return { ok: true, mutacion, aviso: null };
}

/** ¿Hay una intervención mía abierta en OTRO parte del dispositivo? (código del parte). */
export function abiertaEnOtroParte(partes: readonly ParteCampoLocal[], operaciones: readonly Mutacion[], idPt: string): string | null {
  for (const p of partes) {
    if (p.parte.id === idPt) continue;
    const v = proyectarParte(p, operaciones.filter((m) => m.idOt === p.parte.id));
    if (v.abierta !== null) return p.parte.codigo;
  }
  return null;
}
